import { useEffect, useRef, useState } from "react";
import i18n from "../i18n";

const PHOTON_URL = "https://photon.komoot.io/api/";
const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;

// Bounding box restricts Photon results to Sweden (lon_min, lat_min, lon_max, lat_max)
const SWEDEN_BBOX = "10.5,55.2,24.2,69.1";

// Photon only supports: default, de, en, fr
// For Swedish (sv) and other unsupported languages, use "default" which returns
// place names in the local language of the address (e.g. Swedish for SE addresses).
const PHOTON_LANG_MAP = { de: "de", en: "en", fr: "fr" };

function getPhotonLang() {
  const base = (i18n.language ?? "en").split("-")[0].toLowerCase();
  return PHOTON_LANG_MAP[base] ?? "default";
}

function buildLabel({ street, housenumber, postcode, city, country }) {
  const parts = [
    [street, housenumber].filter(Boolean).join(" "),
    postcode,
    city,
    country,
  ].filter(Boolean);
  return parts.join(", ");
}

function mapFeature(feature) {
  const p = feature.properties ?? {};
  // For "street" type results Photon puts the street name in p.name, not p.street
  const streetName = p.street ?? (p.type === "street" ? p.name : undefined);
  return {
    label: buildLabel({
      street: streetName,
      housenumber: p.housenumber,
      postcode: p.postcode,
      city: p.city ?? p.town ?? p.village,
      country: p.country,
    }),
    streetAddress: [streetName, p.housenumber].filter(Boolean).join(" "),
    hasHouseNumber: Boolean(p.housenumber),
    postalCode: p.postcode ?? "",
    city: p.city ?? p.town ?? p.village ?? "",
    country: p.country ?? "",
  };
}

export function useAddressAutocomplete(query) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    controllerRef.current?.abort();

    if (!query || query.length < MIN_CHARS) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const url = new URL(PHOTON_URL);
        url.searchParams.set("q", query);
        url.searchParams.set("limit", "6");
        url.searchParams.set("lang", getPhotonLang());
        url.searchParams.set("bbox", SWEDEN_BBOX);

        const res = await fetch(url.toString(), { signal: controller.signal });
        const data = await res.json();

        const mapped = (data.features ?? [])
          .filter(
            (f) => (f.properties?.countrycode ?? "").toUpperCase() === "SE",
          )
          .map(mapFeature)
          .filter((s) => s.streetAddress)
          // Show complete addresses (with house number) before street-only results
          .sort(
            (a, b) => (b.hasHouseNumber ? 1 : 0) - (a.hasHouseNumber ? 1 : 0),
          );

        setSuggestions(mapped);
        setIsLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setSuggestions([]);
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerRef.current);
      controllerRef.current?.abort();
    };
  }, [query]);

  return { suggestions, isLoading };
}
